import type { DashboardKind, DashboardRecord } from './dash-types';
import {
	ALL_ENERGY_WIDGETS_DASHBOARD_ID,
	ENERGY_DASHBOARD_ID,
} from 'src/components/EnergyMonitoring/v2/energy-v2-types';
import { ALL_DEVICE_WIDGETS_DASHBOARD_ID } from './all-device-widgets';
import { isDashEnergyWidgetType } from './dash-energy-types';

export const CAMERA_DEFAULT_DASHBOARD_ID = -11;
export const PROTECTED_DASHBOARD_IDS = [
	-1,
	-10,
	CAMERA_DEFAULT_DASHBOARD_ID,
	ENERGY_DASHBOARD_ID,
	ALL_ENERGY_WIDGETS_DASHBOARD_ID,
	ALL_DEVICE_WIDGETS_DASHBOARD_ID,
] as const;

export const getDashboardKind = (
	dashboard: Partial<DashboardRecord> | null | undefined,
): DashboardKind => {
	if (dashboard?.kind === 'camera' || dashboard?.kind === 'energy') return dashboard.kind;
	return 'standard';
};

/**
 * Energy dashboards created before the `kind` discriminator was persisted can
 * still be identified by their Energy dashboard configuration. Keep this
 * compatibility check at the dashboard boundary so route-level controls (such
 * as the widget picker) behave consistently for existing saved dashboards.
 */
export const isEnergyDashboard = (dashboard: Partial<DashboardRecord> | null | undefined): boolean =>
	getDashboardKind(dashboard) === 'energy' ||
	Boolean(dashboard?.energyV2) ||
	Boolean(dashboard?.energyV3);

/**
 * Preset-created Energy dashboards intentionally remain ordinary editable
 * dashboards, so they do not carry the protected Energy dashboard metadata.
 * Their widget catalog is still Energy-only and should keep using the focused
 * Energy picker.
 */
export const usesEnergyWidgetCatalog = (
	dashboard: Partial<DashboardRecord> | null | undefined,
): boolean => {
	if (isEnergyDashboard(dashboard)) return true;
	if (!Array.isArray(dashboard?.dashWidgets) || dashboard.dashWidgets.length === 0) return false;

	return dashboard.dashWidgets.every((widget) =>
		isDashEnergyWidgetType((widget as { type?: string })?.type),
	);
};

export const isProtectedDashboard = (
	dashboardOrId: Partial<DashboardRecord> | string | number | null | undefined,
): boolean => {
	if (dashboardOrId && typeof dashboardOrId === 'object') {
		if (dashboardOrId.protected === true) return true;
		return PROTECTED_DASHBOARD_IDS.includes(
			Number(dashboardOrId.id) as (typeof PROTECTED_DASHBOARD_IDS)[number],
		);
	}

	if (dashboardOrId === null || dashboardOrId === undefined || dashboardOrId === '') return false;
	return PROTECTED_DASHBOARD_IDS.includes(
		Number(dashboardOrId) as (typeof PROTECTED_DASHBOARD_IDS)[number],
	);
};
